import { z } from 'zod';

/**
 * The chatbot context bag — a closed vocabulary of *ambient values* shared by the
 * web frontend (the contributor keys it can supply) and the backend tool registry
 * (the zod input fields those keys auto-fill). See `docs/copilot/plan.md` §11.1.
 *
 * The bag is a **hint, not a cage**: it only (a) auto-fills omitted tool args and
 * (b) supplies situational awareness in `<current_context>`. It is never an access
 * boundary — every enabled tool is reachable by name regardless of the bag, and
 * ownership is enforced per-tool via `OWNED_KEYS`/`assertOwned`.
 */
export const CONTEXT_KEYS = [
  // page-local entities
  'symbol',
  'symbols',
  'portfolioId',
  'watchlistId',
  'walletId',
  'timeframe',
  'benchmarks',
  'positionIds',
  'dateRange',
  'displayCurrency',
  // global user layer — present on EVERY page so the model can act on the user's
  // own data anywhere (e.g. a portfolio question answered on a research page).
  'portfolioIds',
  'activePortfolioId',
  'baseCurrency',
  // plural entity lists (Phase 2.1) — exist for the PINNED layer; ambient pages
  // may also contribute them where a list view makes it natural.
  'walletIds',
  'watchlistIds',
] as const;

export type ContextKey = (typeof CONTEXT_KEYS)[number];

/** undefined/empty keys are stripped before send; the bag carries only present keys. */
export type ContextBag = Partial<Record<ContextKey, unknown>>;

/**
 * Keys that name an OWNED resource — ALWAYS ownership-checked server-side before a
 * handler runs (`assertOwned`). A forged owned id resolves to not-found/empty,
 * never another user's data.
 */
export const OWNED_KEYS = ['portfolioId', 'watchlistId', 'walletId'] as const;
export type OwnedKey = (typeof OWNED_KEYS)[number];

/**
 * A ticker symbol as the copilot accepts it (KO, BRK.B, SXR8.DE). ONE source: the page context,
 * the board catalog and the skill brief all validate against it, so a symbol one accepts the
 * others cannot refuse. A plain RegExp, not a zod schema: apps/web bundles this file with zod 3.
 */
export const TICKER_SYMBOL_RE = /^[A-Z0-9.-]{1,12}$/;

/**
 * Per-key validators. The posted bag is **untrusted**: server-side every key is
 * validated and DROPPED on failure (the bag supplies defaults, never authority).
 * `timeframe` is intentionally loose (a bounded string) — the consuming tool's own
 * zod schema does the real validation, so we don't drop legitimate page values.
 */
export const KEY_VALIDATORS: Record<ContextKey, z.ZodType> = {
  symbol: z.string().regex(TICKER_SYMBOL_RE),
  symbols: z.array(z.string().regex(TICKER_SYMBOL_RE)).max(200),
  portfolioId: z.string().uuid(),
  watchlistId: z.string().uuid(),
  walletId: z.string().uuid(),
  timeframe: z.string().min(1).max(40),
  benchmarks: z.array(z.string().regex(TICKER_SYMBOL_RE)).max(10),
  positionIds: z.array(z.string().uuid()).max(500),
  dateRange: z.object({ fromDate: z.string(), toDate: z.string().optional() }),
  displayCurrency: z.string().length(3),
  portfolioIds: z.array(z.string().uuid()).max(200),
  activePortfolioId: z.string().uuid(),
  baseCurrency: z.string().length(3),
  walletIds: z.array(z.string().uuid()).max(200),
  watchlistIds: z.array(z.string().uuid()).max(200),
};

/**
 * Filter an untrusted bag to the closed key set, validate each key, and drop any
 * unknown/invalid/empty entry. Returns a clean `ContextBag` safe to freeze for the
 * turn. A forged-but-valid value still fails the tool's own schema/ownership, so
 * this is the first of several trust layers (plan §11.7).
 */
export function validateBag(raw: ContextBag | null | undefined): ContextBag {
  const out: ContextBag = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const k of CONTEXT_KEYS) {
    const v = raw[k];
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    const r = KEY_VALIDATORS[k].safeParse(v);
    if (r.success) out[k] = r.data;
  }
  return out;
}

/**
 * The PINNED layer (Phase 2.1): the plural-entity subset of the bag a user can pin to a
 * thread. Pinned is a second SOURCE feeding the same vocabulary and trust machinery as the
 * ambient bag — additive on these keys, persisted on the thread, restored with it. Singular
 * ambient keys (`symbol` = where the user stands right now) are never pinnable.
 */
export const PINNED_CONTEXT_KEYS = [
  'symbols',
  'portfolioIds',
  'walletIds',
  'watchlistIds',
] as const;
export type PinnedContextKey = (typeof PINNED_CONTEXT_KEYS)[number];
export type PinnedBag = Partial<Record<PinnedContextKey, unknown>>;

/** App-layer cap on pinned items per thread (summed across the pinned keys). */
export const MAX_PINNED_ITEMS = 20;

/**
 * Validate an untrusted pinned bag: the plural pinned keys only, each through its
 * KEY_VALIDATORS entry, invalid/unknown/empty entries dropped (same posture as
 * {@link validateBag}). The 20-item cap is enforced at the WRITE seam, not here.
 */
export function validatePinnedBag(raw: PinnedBag | null | undefined): PinnedBag {
  const clean = validateBag(raw as ContextBag);
  const out: PinnedBag = {};
  for (const k of PINNED_CONTEXT_KEYS) {
    if (clean[k] !== undefined) out[k] = clean[k];
  }
  return out;
}

/**
 * The ONE bound on copilot instructions — thread (`chat_threads.instructions`) AND global
 * (`copilot_settings.instructions`). Both scopes reach the model through the same
 * `<user_instructions>` assembler, so a length one scope accepts must never be one the other
 * rejects; every DTO's `@MaxLength` and the editor's counter reference THIS constant. The
 * columns are plain `text` on purpose — the cap is an application rule, not a storage one.
 */
export const MAX_COPILOT_INSTRUCTIONS_LENGTH = 2048;

/** App-layer cap on stored copilot memories per user (`record_memory` refuses over this). */
export const MAX_MEMORIES = 50;

/**
 * App-layer cap on one memory's text. 300 until 2026-10-01: the AI SDK refused a `record_memory`
 * call over the cap before it ran, and three of the eight such refusals on 2026-09-30 were
 * ordinary Polish preferences of ~400 characters — a saved investing plan, a model portfolio.
 * The ceiling it guards is the prompt: `<user_memory>` is sent every turn, MAX_MEMORIES of them.
 */
export const MAX_MEMORY_LENGTH = 1000;

/** `pageContext` posted by the web app each turn. */
export interface PageContext {
  /** Derived from the route (`place` = page+tab, e.g. `research:overview`). */
  place: string;
  /** The ambient values the current place can supply. */
  bag: ContextBag;
}
